import React from 'react';
import cn from 'classnames';
import Avatar from '../Avatar';
import styles from './ContactAvatar.module.scss';
import Contact from '@models/Contact';
import ContactLink from '@components/ContactLink';
import Tooltip, { Overlay } from '@components/Tooltip';
import { ID } from '@app/types';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

export function ContactAvatarGroup({ contacts, displayCount = 2 }) {
  return (
    <div className={styles.avatarGroup}>
      {contacts
        .slice(0, displayCount)
        .reverse()
        .map((contact) => (
          <div className={styles.avatarGroupMember} key={contact.id}>
            <ContactAvatar contact={contact} size="sm" />
          </div>
        ))}
    </div>
  );
}

export function ImportContactAvatarGroup({
  contacts,
  displayCount = 3,
  importTotal,
}: {
  contacts: Contact[];
  displayCount?: number;
  importTotal: number;
}) {
  const additionalContacts = importTotal - displayCount;
  return (
    <div className={styles.importAvatarGroup}>
      {contacts.slice(0, displayCount).map((contact) => (
        <div className={styles.avatarGroupMember} key={contact.id}>
          <ContactAvatar contact={contact} className={styles.importAvatar} />
        </div>
      ))}

      {additionalContacts > 0 && (
        <div className={cn(styles.avatarGroupMember, styles.additionalContactCount)}>
          <div>
            {additionalContacts <= 99 ? '+' : ''}
            {additionalContacts}
            {additionalContacts > 99 ? '+' : ''}
          </div>
        </div>
      )}
    </div>
  );
}

export const ReconnectContactAvatarList = ({
  contacts,
  className,
  contactIsDismissed,
}: {
  contacts: Contact[];
  className?: string;
  contactIsDismissed?: (contactID: ID) => boolean;
}) =>
  contacts &&
  contacts.length > 0 && (
    <TransitionGroup className={cn(styles.reconnectContactAvatarList, className)}>
      {contacts.map((contact) => (
        <CSSTransition
          key={contact.id}
          timeout={200}
          classNames={{
            exit: styles.exit,
            exitActive: styles.exitActive,
          }}
        >
          <ContactLink contact={contact} className={styles.reconnectAvatar}>
            <Tooltip overlay={<Overlay>{contact.displayName}</Overlay>} placement="bottom">
              <ContactAvatar
                contact={contact}
                className={cn(
                  styles.reconnectAvatar,
                  contactIsDismissed(contact.id) && styles.dismissed,
                )}
              />
            </Tooltip>
          </ContactLink>
        </CSSTransition>
      ))}
    </TransitionGroup>
  );

export function AngledContactGroup({ contacts }) {
  return (
    <div className={styles.angledAvatarGroup}>
      {contacts
        .slice(0, 2)
        .reverse()
        .map((contact) => (
          <div className={styles.avatarGroupMember} key={contact.id}>
            <ContactAvatar contact={contact} size="sm" />
          </div>
        ))}
    </div>
  );
}

export default function ContactAvatar({ contact, ...props }) {
  if (!contact) {
    return null;
  }

  const name = contact.displayName || contact.display_name || contact.firstName || '';
  let initial = (name[0] || '').toUpperCase();

  return (
    <Avatar
      {...props}
      url={contact.avatarURL || contact.avatar_url}
      initials={initial}
      name={name}
      grayscale={contact.isMemorialized || props.grayscale}
    />
  );
}
